import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import { forwardRef, type ReactNode, type Ref } from 'react';
import { NavLink } from '../NavLink';
import { Body3 } from '../typography';

export const NavigationMenu = forwardRef(
  (
    { children, ...props }: NavigationMenuPrimitive.NavigationMenuProps,
    forwardedRef: Ref<HTMLDivElement>,
  ) => {
    return (
      <NavigationMenuPrimitive.Root
        {...props}
        ref={forwardedRef}
        className="z-1 flex items-center justify-center"
      >
        <NavigationMenuPrimitive.List className="text-text-primary m-0 flex list-none items-center justify-center gap-24 p-0">
          {children}
        </NavigationMenuPrimitive.List>
        <div
          className="absolute left-0 top-full flex w-full justify-center"
          // TODO: when we upgrade to tailwind 4.0 we can add perspective here as a class.
          style={{ perspective: '2000px' }}
        >
          <NavigationMenuPrimitive.Viewport className="bg-background transition-width,height 300ms ease transform-origin-top-center [&[data-state='open']]:animate-scale-in [&[data-state='close']]:animate-scale-out relative h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden rounded-b-[6px]" />
        </div>
      </NavigationMenuPrimitive.Root>
    );
  },
);

NavigationMenu.displayName = 'NavigationMenu';

export const NavigationMenuItem = NavigationMenuPrimitive.Item;

export const NavigationMenuTrigger = ({
  children,
}: NavigationMenuPrimitive.NavigationMenuItemProps) => {
  return (
    <NavigationMenuPrimitive.Trigger
      asChild
      className="user-select-none text-text-primary flex items-center justify-between outline-none"
    >
      <NavLink
        withIcon
        variant="primary"
        className="text-text-primary hidden lg:inline-flex"
      >
        {children}
      </NavLink>
    </NavigationMenuPrimitive.Trigger>
  );
};

interface NavigationMenuItemContentProps
  extends NavigationMenuPrimitive.NavigationMenuContentProps {
  imgSrc?: string;
  imgAlt?: string;
  title: string;
}
export const NavigationMenuItemContent = forwardRef(
  (
    {
      children,
      imgAlt,
      imgSrc,
      title,
      ...props
    }: NavigationMenuItemContentProps,
    forwardedRef: Ref<HTMLDivElement>,
  ) => {
    return (
      <NavigationMenuPrimitive.Content
        {...props}
        ref={forwardedRef}
        className="bg-grey-5 dark:bg-slate-70 [&[data-motion='from-start']]:animate-enter-from-left [&[data-motion='from-end']]:animate-enter-from-right [&[data-motion='to-start']]:animate-exit-to-left [&[data-motion='to-end']]:animate-exit-to-right absolute left-0 top-0 flex w-full"
      >
        <div className="bg-background-alternate-consistent h-[355px] min-h-[355px] flex-[1_0_50%]">
          <img
            src={imgSrc}
            alt={imgAlt}
            className="h-full w-full object-cover object-bottom"
          />
        </div>
        <div className="flex-[1_0_50%] p-48">
          <Body3 className="mb-32" caps>
            {title}
          </Body3>
          {children}
        </div>
      </NavigationMenuPrimitive.Content>
    );
  },
);

NavigationMenuItemContent.displayName = 'NavigationMenuItemContent';

interface NavigationMenuLinkProps {
  href?: string;
  children: ReactNode;
}
export const NavigationMenuLink = ({
  children,
  href,
}: NavigationMenuLinkProps) => {
  return (
    <NavLink variant="secondary" href={href}>
      {children}
    </NavLink>
  );
};
